import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import Axis from '../Axis/index.js';
import { VictoryArea, VictoryScatter, VictoryStack, VictoryLine, VictoryChart, VictoryGroup, VictoryTooltip, VictoryLabel, VictoryContainer } from 'victory';
import sizes from '../sizes.js';
import colors from '../../../utils/colors.js';
import { useMaturityLevel } from '../../../component-maturity.js';

const connectPhases = (data) => {
  const actual = _.get(data, '0') || [];
  const projection = _.get(data, '1') || [];
  const connectedProjection = (
    _.some(projection) && _.some(actual)
  ) ? _.map(projection, (projectionGroup, groupIndex) => (
    [
      _.last(_.get(actual, groupIndex)),
      ..._.map(projectionGroup, entry => _.merge({}, entry, {
        xValue: entry.xValue + _.get(actual, groupIndex).length
      }))
    ]
  )) : [];

  return [actual, connectedProjection];
};

const parse = ({ data, empty, isEmpty }) => {
  if (isEmpty) {
    return [[[{
      label: empty,
      xIndex: 'x|0|0',
      xValue: 1,
      value: 1,
      tooltip: empty,
      color: colors.grey200
    }, {
      label: empty,
      xIndex: 'x|0|0',
      xValue: 2,
      value: 1,
      tooltip: empty,
      color: colors.grey200
    }]]];
  }
  const withNumericValues = _.map(data, phase => _.map(phase, entries => _.map(entries, (entry) => (
    _.merge({}, entry, { value: +_.get(entry, 'value') || 0 })
  ))));
  const withColors = _.map(withNumericValues,
    (phase, phaseIndex) => _.map(phase, (entries, groupIndex) => _.map(entries, (entry, entryIndex) => {
      const indexForColor = 1 - groupIndex;
      return _.merge({}, entry, {
        xValue: entryIndex + 1,
        xIndex: `x|${phaseIndex}|${entryIndex + phaseIndex}`,
        phaseIndex,
        entryIndex,
        groupIndex,
        color: entry.color || _.get(colors.order, indexForColor % colors.order.length, 'black')
      });
    }))
  );
  return connectPhases(withColors);
};

const tickValuesFor = (data, isEmpty) => {
  const tickValuesForX = _.uniq(_.flatMap(data, phase => _.flatMap(phase, entries => _.map(entries, 'label'))));
  if (isEmpty) {
    return { x: tickValuesForX, y: [1] };
  }
  const tickValuesForY = _.uniq(_.orderBy(_.flatMap(data, phase => _.flatMap(phase, entries => _.map(entries, 'value')))));

  return { x: tickValuesForX, y: tickValuesForY };
};

const tickFormatsFor = (data, isEmpty, empty, format) => {
  if (isEmpty) {
    return { x: format.x, y: () => empty };
  }
  return { x: format.x, y: format.y };
};

const axis = ({
  axisLabelPadding,
  tickValues,
  tickFormats,
  isEmpty,
  label
}) => {
  return [
    Axis.X({
      padding: axisLabelPadding.x,
      tickValues: tickValues.x,
      tickFormats: tickFormats.x,
      isEmpty: isEmpty,
      label: label.x
    }),
    Axis.Y({
      padding: axisLabelPadding.y,
      tickFormats: tickFormats.y,
      isEmpty: isEmpty,
      label: label.y
    })
  ];
};

// this is needed because of how victory calculates layout
const hardcodedTooltip = { fontSize: 9 };
const points = ({ lineHeight, openTooltips, tooltipClasses, pointWidth, pointSize } = {}) => {
  return (
    <VictoryScatter
      labels={({ valueX, value, tooltip } = {}) => (
        (valueX && value && tooltip) ? tooltip : null
      )}
      labelComponent={
        <VictoryTooltip
          active={openTooltips}
          text={_.property('tooltip')}
          labelComponent={
            <VictoryLabel
              style={hardcodedTooltip}
              className={tooltipClasses}
              lineHeight={lineHeight}
            />
          }
          flyoutStyle={{
            stroke: _.property('color'),
            fill: 'white'
          }}
        />
      }
      size={pointSize}
      style={{
        data: {
          cursor: 'pointer',
          display: ({ value } = {}) => ((value <= 0) ? 'none' : undefined),
          fill: () => 'white',
          stroke: _.property('color'),
          strokeWidth: pointWidth
        }
      }}
    />
  );
};
const line = ({ phaseIndex, lineWidth } = {}) => {
  return (
    <VictoryLine labelComponent={<g />} style={{
      data: {
        strokeWidth: lineWidth,
        strokeDasharray: (!!phaseIndex ? `${lineWidth}, ${lineWidth}` : '1, 0'),
        stroke: entries => (
          _.some(entries, ({ value } = {}) => (value > 0)) ? _.get(entries, [0, 'color']) : 'transparent'
        )
      }
    }} />
  );
};
const area = () => {
  return (
    <VictoryArea labelComponent={<g />} style={{
      data: { fill: _.property('0.color'), opacity: 0.15 }
    }} />
  );
};

const StackedArea = ({
  size,
  openTooltips,
  className,
  data,
  empty,
  width,
  height,
  domainPaddingX,
  domainPaddingY,
  lineHeight,
  label,
  format,
  axisLabelPadding,
  pointWidth,
  lineWidth,
  pointSize
}) => {
  useMaturityLevel('deprecated', 'StackedArea');
  const sizedWidth = width || sizes.width(size);
  const sizedHeight = height || sizes.height(size);
  const isEmpty = !_.some(data) || !_.some(data, phases => _.some(phases)) ||
    !_.some(data, phase => _.some(phase, entries => _.some(entries)));
  const parsed = parse({ data, empty, isEmpty });
  const classes = classnames('particles-stacked-area-chart', className, {
    'particles-stacked-area-chart--empty': !!isEmpty
  });
  const tooltipClasses = classnames('particles-stacked-area-chart__tooltip', {
    'particles-stacked-area-chart__tooltip--empty': !!isEmpty
  });
  const tickValues = tickValuesFor(parsed, isEmpty, empty);
  const tickFormats = tickFormatsFor(parsed, isEmpty, empty, format);

  return (
    <div className={classes}>
      <VictoryChart
        height={sizedHeight}
        width={sizedWidth}
        domainPadding={{ x: domainPaddingX, y: [0, domainPaddingY] }}
        containerComponent={<VictoryContainer responsive={false} style={{ overflow: 'visible' }} />}
        style={{ parent: { overflow: 'visible' } }}
      >
        {axis({ tickValues, tickFormats, isEmpty, label, axisLabelPadding })}
        {_.map(parsed, (phase, phaseIndex) => {
          return (_.some(phase) && _.every(phase, group => (group && (group.length > 1))) && (
            <VictoryStack key={`phase-index-${phaseIndex}`}>
              {_.map(phase, (group, groupIndex) => {
                return (group && group[0] && group[1] && (
                  <VictoryGroup
                    key={`group-${groupIndex}`}
                    data={group}
                    x={_.property('xValue')}
                    y={_.property('value')} >
                    {line({ phaseIndex, lineWidth })}
                    {area({})}
                    {points({ lineHeight, openTooltips, tooltipClasses, pointWidth, pointSize })}
                  </VictoryGroup>
                ));
              })}
            </VictoryStack>
          ));
        })}
      </VictoryChart>
    </div>
  );
};

StackedArea.propTypes = {
  /** Width in pixels of lines in the graph */
  lineWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Size in pixels of points in the graph */
  pointWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Size in pixels of points in the graph? */
  pointSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Padding around text labels of each axis */
  axisLabelPadding: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  /** Value display formatter. You can pass a function for both `total` and `others` values. Your custom
   * function is passed a value and should return a string.
   * ie: `(value) => string`
   */
  format: PropTypes.shape({
    x: PropTypes.func,
    y: PropTypes.func
  }),
  /** Size of the chart, ['small', 'medium', 'large'] */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Additional class to add to component */
  className: PropTypes.string,
  /** Toggle display of tooltips */
  openTooltips: PropTypes.bool,
  minStackedAreaWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxStackedAreaWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Number of pixels of padding to add the beginning or end of a domain. This prop is useful for explicitly spacing data elements farther from the beginning or end of a domain to prevent axis crowding. NOTE: Not recommended to modify in normal circumstances */
  domainPaddingY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Number of pixels of padding to add the beginning or end of a domain. This prop is useful for explicitly spacing data elements farther from the beginning or end of a domain to prevent axis crowding. NOTE: Not recommended to modify in normal circumstances */
  domainPaddingX: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Specify a static width in pixels of chart. NOTE: Not recommended to modify in normal circumstances */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Specify a static height in pixels of chart. NOTE: Not recommended to modify in normal circumstances */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Line height for text in the chart. NOTE: Not recommended to modify in normal circumstances */
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Placeholder text to show when chart has no data */
  empty: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Specifies labels to display for x or y axis */
  label: PropTypes.shape({ x: PropTypes.string, y: PropTypes.string }),
  /** Array of data to display in chart. Values for the array can have the following keys
   * "label", "color", "value", "tooltip".
  */
  data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    color: PropTypes.oneOfType([PropTypes.string]),
    label: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    tooltip: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
  })))).isRequired
};

StackedArea.defaultProps = {
  lineWidth: 3,
  pointWidth: 3,
  pointSize: 5,
  axisLabelPadding: {
    x: 40,
    y: 50
  },
  format: {
    x: _.identity,
    y: _.identity
  },
  size: 'medium',
  className: '',
  label: { x: '', y: '' },
  openTooltips: false,
  domainPaddingY: 10,
  domainPaddingX: 10,
  lineHeight: 2,
  empty: 'No Data',
  data: [[[]]]
};

StackedArea.displayName = 'Chart.StackedArea';

export default StackedArea;

export const {
  propTypes,
  defaultProps,
  displayName
} = StackedArea;
